import { api } from './api';
import { CommissionTreatment, FaultParty, FeeBorneBy } from '../types/refunds';
import { AssignableProvider, TransportType } from '../types/delivery';

/**
 * #918 — one picked option of a multi-select booking (list price, before any offer). #1193 — every
 * order now carries its lines: a single-variant pick is one line labelled with the picked values
 * ("Large · Premium"), and a flat service is one line labelled with the service name itself.
 */
export interface ServiceLine {
  variantId: string | null;
  variationValueId: string | null;
  label: string | null;
  price: number;
  durationMinutes: number | null;
}

export interface AdminOrderResponse {
  id: string;
  /** #1181 — the day the customer reserved (ISO yyyy-MM-dd) and its pickup slot label. */
  scheduledDate?: string | null;
  pickupTimeSlotLabel?: string | null;
  orderNumber: string | null;
  customerId: string;
  garageId: string;
  serviceOfferingId: string;
  status: string;
  totalAmount: number;
  currency: string;
  paymentId: string | null;
  settlementId: string | null;
  refundId: string | null;
  isSettled: boolean;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  customerName: string | null;
  garageName: string | null;
  /** #1193 — on the list too. Optional on the wire: an orders build from before #1193 omits them. */
  serviceName?: string | null;
  serviceLines?: ServiceLine[] | null;
  // ── Spec 053 Phase 3 (Flow F1/F4) — Garagat's own pickup & delivery supply ──
  // Optional on the wire: an orders build from before the fee shipped omits them, and every
  // consumer here reads that as "this order carries no delivery fee".
  /**
   * VAT-inclusive fee Garagat charged the customer for the trips.
   *
   * `totalAmount` does NOT include it. `orders.total_amount` is and always has been the GARAGE's
   * service gross (`BookingService` writes the offering price, `OrderService` copies it); the fee
   * lives in its own columns and only `price_breakdown.totalPayable` carries service + fee. The two
   * supplies are billed on two documents, so nothing on this payload is what the customer paid.
   */
  deliveryFeeGross?: number | null;
  /** Chargeable Garagat trips (0–2). Never derive the legs from this — see progress.md S10. */
  deliveryTrips?: number | null;
}

/**
 * The service slice of an order — what the garage supplied, and the only basis a service refund or
 * a GCN may be measured against.
 *
 * It is `totalAmount` unchanged: see the field's note above. This helper exists so every caller
 * states which slice it means rather than passing `totalAmount` around and hoping, and so the one
 * place would change if `total_amount`'s meaning ever did.
 */
/**
 * #1193 — the customer's selection: the order's lines minus the ones that only repeat the service.
 * A flat service's single line is labelled with the service name, and a line without a label says
 * nothing, so both drop out; an empty result means there is no selection to show.
 */
export function selectedOptions(order: Pick<AdminOrderResponse, 'serviceName' | 'serviceLines'>): ServiceLine[] {
  const labelled = (order.serviceLines ?? []).filter((l) => l.label?.trim());
  const name = order.serviceName?.trim();
  if (labelled.length === 1 && labelled[0].label!.trim() === name) return [];
  return labelled;
}

/**
 * #1181 — "Sat, 24 Oct 2026 · Morning (08:00–12:00)". The date is a calendar day, not an instant, so it is
 * built from its parts: `new Date('2026-10-24')` is UTC midnight and would show the 23rd west of UTC.
 */
export function formatScheduled(date: string | null | undefined, slot?: string | null): string | null {
  const m = date ? /^(\d{4})-(\d{2})-(\d{2})/.exec(date) : null;
  if (!m) return null;
  const day = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
  return slot ? `${day} · ${slot}` : day;
}

/** Spec 077 — which part of the journey a reschedule changes. */
export type ReschedulePhase = 'PICKUP' | 'RETURN';

export interface RescheduleSlot {
  id: string;
  label: string;
  startTime: string;
  endTime: string;
}

export interface RescheduleEditable {
  date: boolean;
  pickupSlot: boolean;
  returnDate: boolean;
  returnSlot: boolean;
}

/** The server sends `editable` as the request field names the admin may send (#1218 stage QA). */
export function toRescheduleEditable(fields: readonly string[] | null | undefined): RescheduleEditable {
  const has = (f: string) => !!fields?.includes(f);
  return {
    date: has('date'),
    pickupSlot: has('pickupTimeSlotId'),
    returnDate: has('returnDate'),
    returnSlot: has('returnTimeSlotId'),
  };
}

/** `GET /api/admin/orders/{id}/reschedule-options?date=` */
export interface RescheduleOptions {
  allowed: boolean;
  reason?: string | null;
  /** Human-readable form of `reason`. */
  reasonMessage?: string | null;
  phase: ReschedulePhase | null;
  editable: RescheduleEditable;
  current: {
    date: string | null;
    pickupTimeSlotId: string | null;
    returnDate: string | null;
    returnTimeSlotId: string | null;
  };
  slots: RescheduleSlot[];
  capacity: { date: string; used: number; capacity: number; full: boolean } | null;
  /** Zofeur orders only: does Zofeur still cover the address? null for other orders. */
  zofeurCovered: boolean | null;
}

/** `POST /api/admin/orders/{id}/reschedule` */
export interface RescheduleRequest {
  date?: string;
  pickupTimeSlotId?: string;
  returnDate?: string;
  returnTimeSlotId?: string;
  garageAgreed: boolean;
  note?: string;
  expectedVersion: number;
}

export function serviceGrossOf(order: Pick<AdminOrderResponse, 'totalAmount' | 'deliveryFeeGross'>): number {
  return Math.round(order.totalAmount * 100) / 100;
}

export interface AdminOrderDetailResponse extends AdminOrderResponse {
  bookingId: string;
  vehicleId: string;
  refundEligible: boolean;
  refundWindowExpiresAt: string | null;
  /**
   * #468 — the refund block. `refundId` alone carried no money, so ops could not see what was
   * actually returned. All null until the order is refunded; the credit-note pair stays null until
   * the note is ISSUED (a number is only allocated at issuance). Optional on the wire: an orders
   * build from before #468 omits them.
   */
  refundedAmount?: number | null;
  refundedAt?: string | null;
  refundTransactionRef?: string | null;
  creditNoteNumber?: string | null;
  creditNoteDocumentId?: string | null;
  notes: string | null;
  customerPhone: string | null;
  vehiclePlate: string | null;
  vehicleMakeModel: string | null;
  serviceName: string | null;
  pickupTimeSlotLabel: string | null;
  /**
   * Spec 077 (#1218) — what the reschedule modal needs. Optional on the wire: an orders build from
   * before the feature omits them, and the page then reads `rescheduleAllowed` as false.
   */
  pickupTimeSlotId?: string | null;
  returnTimeSlotId?: string | null;
  returnTimeSlotLabel?: string | null;
  /** ISO yyyy-MM-dd; null = the return is on the booking day. */
  returnDate?: string | null;
  /** Switch ON + paid + a reschedulable status + the leg not under way. */
  rescheduleAllowed?: boolean;
  reschedulablePhase?: ReschedulePhase | null;
  /** The order's optimistic-lock version; sent back as `expectedVersion`. */
  version?: number;
  resolutionMessage: string | null;
  resolutionRefundPercent: number | null;
  /**
   * #634 / #641 — who moves the car on each leg, per `AdminOrderDetailResponse.pickupMethod` /
   * `returnMethod` (orders `LegMethod`). Resolved server-side through `Order.effective*Method()`,
   * so an order from before the per-leg columns still answers. Optional on the wire: an older
   * orders build omits them, and the page then treats the method as unknown — it keeps offering
   * the assign button rather than guessing SELF.
   */
  pickupMethod?: LegMethod | null;
  returnMethod?: LegMethod | null;
  /**
   * Spec 072 v2 — the transport the customer picked: `transportType ?? (any leg GARAGAT ? RECOVERY
   * : null)` server-side, so null only when no leg is Garagat's. Optional on the wire for an orders
   * build before v2.
   */
  transportType?: TransportType | null;
  timeline: OrderTimelineEvent[];
  pickupDeliveries: PickupDeliveryResponse[];
  /**
   * Spec 059 — the admin view of the order's price breakdown (`OrderBreakdownView.admin()`):
   * the partner figures plus the Garagat promotion. Optional on the wire; absent or a null
   * `promoDiscount` means the order used no code.
   */
  breakdown?: AdminOrderBreakdown | null;
  /** Spec 068 — Garagat's service fee on the order, VAT-inclusive; null/0 = no fee (mirrors `breakdown`). */
  serviceFeeGross?: number | null;
}

/** Spec 059 design §3 — `GET /api/admin/orders/{id}` → `breakdown` (admin view). */
export interface AdminOrderBreakdown {
  serviceNet?: number | null;
  vatAmount?: number | null;
  vatApplicable?: boolean;
  platformFee?: number | null;
  platformFeeVat?: number | null;
  /** What the customer paid: service + Garagat delivery fee − promoDiscount. */
  totalPayable?: number | null;
  commissionAmount?: number | null;
  partnerNet?: number | null;
  promoCode?: string | null;
  promoDiscount?: number | null;
  promoCodeId?: string | null;
  /**
   * Spec 068 — Garagat's platform service fee frozen on the booking (VAT-inclusive gross, its net
   * and VAT). The backend sends 0.00 or null when the order carries no fee: both mean "no fee row".
   */
  serviceFeeGross?: number | null;
  serviceFeeNet?: number | null;
  serviceFeeVat?: number | null;
  /** Spec 068 — what the customer was charged, on every order (with or without a promo). */
  amountPaid?: number | null;
}

/** Spec 068 §0b — a fee figure is shown iff it is > 0; null, absent and 0.00 all mean "no fee". */
export function positiveOrNull(value: number | null | undefined): number | null {
  return value != null && value > 0 ? value : null;
}

/** Spec 068 V9 — the service fee on an order (gross + VAT), or null when it carries none. */
export function orderServiceFee(order: Pick<AdminOrderDetailResponse, 'breakdown' | 'serviceFeeGross'>): {
  gross: number; vat: number | null;
} | null {
  const gross = positiveOrNull(order.breakdown?.serviceFeeGross ?? order.serviceFeeGross);
  if (gross == null) return null;
  return { gross, vat: positiveOrNull(order.breakdown?.serviceFeeVat) };
}

/** Spec 068 AC-24 — "Amount paid" on every order: `amountPaid`, falling back to `totalPayable`. */
export function orderAmountPaid(order: Pick<AdminOrderDetailResponse, 'breakdown'>): number | null {
  return order.breakdown?.amountPaid ?? order.breakdown?.totalPayable ?? null;
}

/** The Garagat promotion on an order, or null when none applied (Spec 059 AC-66). */
export function orderPromotion(order: Pick<AdminOrderDetailResponse, 'breakdown'>): {
  code: string; discount: number; codeId: string | null; amountPaid: number | null;
} | null {
  const b = order.breakdown;
  if (!b || !b.promoDiscount || b.promoDiscount <= 0) return null;
  return { code: b.promoCode ?? '', discount: b.promoDiscount, codeId: b.promoCodeId ?? null, amountPaid: b.totalPayable ?? null };
}

/** orders `LegMethod`: Garagat's driver moves the car, or the customer does it themselves. */
export type LegMethod = 'GARAGAT' | 'SELF';

export interface AssignDriverRequest {
  driverName?: string;
  driverPhone?: string;
  /**
   * Spec 053 Flow F3 / BR-27, #633 — the recovery provider performing this Garagat leg, chosen from
   * the active registry. There is no default: a first assignment without it is refused (422).
   * On a reassignment, omitting it keeps the leg's existing provider snapshot (BR-26); naming one
   * re-snapshots it.
   */
  providerId?: string;
}

export interface ResolveDisputeRequest {
  outcome: 'CLOSE' | 'RETURN_TO_SERVICE';
  refundPercent?: number;
  message: string;
  // Spec 053 BR-4 / BR-7 — required by the backend whenever a refund is issued (400 otherwise);
  // forwarded to billing untouched. feeBorneBy omitted → billing's default (#1059: PLATFORM for
  // Garagat's own fault, CUSTOMER otherwise).
  faultParty?: FaultParty;
  feeBorneBy?: FeeBorneBy;
  /**
   * Spec 053 BR-25 / AC-40 — the custom AED amount against the SERVICE slice. The percentage is a
   * shortcut that fills this field (Flow F5), and it is computed off service gross, never off
   * `totalAmount`, which now carries Garagat's delivery fee too (delta D27).
   * Omitted for a 100% refund, so billing refunds the service ceiling as it always has.
   */
  refundAmount?: number;
  /** BR-25 — also reverse the pickup & delivery fee (issues a PCN). */
  refundDeliveryFee?: boolean;
  /** Spec 068 — refund Garagat's service fee; omitted → the policy default for the fault party. */
  refundServiceFee?: boolean;
}

export interface VehicleResponse {
  id: string;
  label: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  color: string | null;
  plateNumber: string | null;
  vehicleType: string | null;
  isDefault: boolean;
  /** @deprecated Backend still sends it, but it is only image #1 — read registrationLicenseUrls. */
  registrationLicenseUrl: string | null;
  /** Presigned URLs for every registration-license photo (up to 2), in display order. */
  registrationLicenseUrls: string[] | null;
  createdAt: string;
}

// Raw shape as serialized by the backend — note the JSON key is `default`, not `isDefault`.
interface RawVehicleResponse extends Omit<VehicleResponse, 'isDefault'> {
  default: boolean;
}

/**
 * One order event (`OrderEventEntry`). The single timeline type for both admin order payloads
 * (`ordersApi` detail and `api.getAdminOrderDetail`). Spec 069 adds the resolved actor.
 */
export interface OrderTimelineEvent {
  id: string;
  eventType: string;
  eventData: Record<string, string>;
  actorId: string | null;
  actorRole: string;
  createdAt: string;
  /** Spec 069 — the actor's display name; null when it could not be resolved. */
  actorName?: string | null;
  /** Spec 069 — "ADMIN · Maher", "GARAGE OWNER · Ali (Speedy)", "CUSTOMER · Sara", "SYSTEM". */
  actorLabel?: string | null;
  /** Spec 069 — the garage of a garage-member actor. */
  garageName?: string | null;
}

export interface PickupDeliveryResponse {
  id: string;
  type: string;
  status: string;
  driverName: string | null;
  driverPhone: string | null;
  scheduledAt: string | null;
  completedAt: string | null;
  pickupAddress: string | null;
  deliveryAddress: string | null;
  notes: string | null;
  /**
   * Spec 053 BR-26 — the provider snapshot frozen onto this leg at driver assignment (admin views
   * only). Null until a driver is assigned, and on legs assigned before the registry existed.
   * Optional on the wire so an older orders build that omits them still parses.
   */
  providerId?: string | null;
  providerName?: string | null;
  /** `CONTRACTOR | IN_HOUSE`, frozen with the provider. */
  providerType?: string | null;
  /**
   * #634 — the provider's cost per trip, ex-VAT, frozen at assignment; it is what the recorded trip
   * carries into a provider settlement. `null` = no cost configured, which is NOT a cost of zero.
   */
  providerCostPerTrip?: number | null;
  /**
   * Spec 072 — the insurance cover and disclaimer frozen onto the leg at assignment (admin views
   * only; the server omits the keys when null). `providerDisclaimerEn` is the template the customer's
   * note was built from, `{amount}` unfilled.
   */
  providerInsuredAmount?: number | null;
  providerShowDisclaimer?: boolean | null;
  providerDisclaimerEn?: string | null;
  providerDisclaimerAr?: string | null;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export type OrderTab = 'NEEDS_ACTION' | 'AWAITING_DRIVER' | 'DISPUTED' | 'ACTIVE' | 'CLOSED' | 'ALL';

export interface OrderListParams {
  search?: string;
  tab?: OrderTab;
  status?: string;
  garageId?: string;
  customerId?: string;
  dateFrom?: string;
  dateTo?: string;
  /** #1181 — the reserved day, inclusive. */
  scheduledFrom?: string;
  scheduledTo?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export interface ProcessRefundRequest {
  refundType: 'FULL' | 'PARTIAL';
  /** The SERVICE slice (BR-25). The delivery fee is requested with `refundDeliveryFee`. */
  amount?: number;
  reason: string;
  /** Spec 053 BR-25 / AC-40 — also reverse Garagat's pickup & delivery fee (issues a PCN). */
  refundDeliveryFee?: boolean;
  /** Spec 068 — refund Garagat's service fee; omitted → the policy default for the fault party. */
  refundServiceFee?: boolean;
  // Spec 053 Flow B — fault attribution (BR-4) + fee bearer (BR-8b). Same fields
  // as POST /api/admin/refunds/{id}/approve; the backend 400s when faultParty is missing.
  faultParty?: FaultParty;
  feeBorneBy?: FeeBorneBy;
  commissionTreatment?: CommissionTreatment;
}

/**
 * Billing's POST /api/admin/orders/{orderId}/refund takes the order context as query params
 * (bookingId, orderAmount, completedAtStr) next to the JSON body — this is the slice of the
 * loaded order it needs.
 */
export type RefundOrderContext = Pick<AdminOrderDetailResponse, 'bookingId' | 'totalAmount' | 'completedAt'>;

/**
 * Orders serialises completedAt as a zone-less LocalDateTime ("2026-09-20T10:00:00.123456", UTC).
 * Billing parses it with Instant.parse, which requires an offset — normalise to "...Z".
 */
export function toInstantIso(localDateTime: string): string {
  const hasOffset = /(Z|[+-]\d{2}:\d{2})$/.test(localDateTime);
  return new Date(hasOffset ? localDateTime : `${localDateTime}Z`).toISOString();
}

export const ordersApi = {
  listOrders: (params: OrderListParams, token: string) => {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.tab && params.tab !== 'ALL') query.set('tab', params.tab);
    if (params.status) query.set('status', params.status);
    if (params.garageId) query.set('garageId', params.garageId);
    if (params.customerId) query.set('customerId', params.customerId);
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.scheduledFrom) query.set('scheduledFrom', params.scheduledFrom);
    if (params.scheduledTo) query.set('scheduledTo', params.scheduledTo);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.size !== undefined) query.set('size', String(params.size));
    if (params.sort) query.set('sort', params.sort);
    const qs = query.toString();
    return api.get<PageResponse<AdminOrderResponse>>(
      `/api/admin/orders${qs ? `?${qs}` : ''}`,
      token
    );
  },

  getOrderDetail: (orderId: string, token: string) =>
    api.get<AdminOrderDetailResponse>(`/api/admin/orders/${orderId}`, token),

  processRefund: (orderId: string, order: RefundOrderContext, request: ProcessRefundRequest, token: string) => {
    const query = new URLSearchParams();
    query.set('bookingId', order.bookingId);
    query.set('orderAmount', String(order.totalAmount));
    if (order.completedAt) query.set('completedAtStr', toInstantIso(order.completedAt));
    return api.post<unknown>(`/api/admin/orders/${orderId}/refund?${query.toString()}`, request, token);
  },

  /**
   * #633 — active recovery providers for the Assign/Reassign Driver picker (id, name, type only).
   * Served under /api/admin/orders so the `orders` screen is enough: the full registry at
   * /api/admin/delivery/providers is a Finance surface an ops admin may not hold.
   *
   * Spec 072 v2 — `transportType` narrows the list to the order's type (a Zofeur order lists Zofeur
   * drivers only); absent lists every active provider, as before.
   */
  listAssignableProviders: (token: string, transportType?: TransportType) =>
    api.get<AssignableProvider[]>(
      transportType
        ? `/api/admin/orders/recovery-providers?transportType=${encodeURIComponent(transportType)}`
        : '/api/admin/orders/recovery-providers',
      token,
    ),

  // Spec 048 — order journey admin actions
  assignPickupDriver: (orderId: string, request: AssignDriverRequest, token: string) =>
    api.post<AdminOrderDetailResponse>(`/api/admin/orders/${orderId}/assign-pickup-driver`, request, token),

  assignDropoffDriver: (orderId: string, request: AssignDriverRequest, token: string) =>
    api.post<AdminOrderDetailResponse>(`/api/admin/orders/${orderId}/assign-dropoff-driver`, request, token),

  reassignPickupDriver: (orderId: string, request: AssignDriverRequest, token: string) =>
    api.post<AdminOrderDetailResponse>(`/api/admin/orders/${orderId}/reassign-pickup-driver`, request, token),

  reassignDropoffDriver: (orderId: string, request: AssignDriverRequest, token: string) =>
    api.post<AdminOrderDetailResponse>(`/api/admin/orders/${orderId}/reassign-dropoff-driver`, request, token),

  // Spec 077 (#1218) — admin reschedule
  getRescheduleOptions: (orderId: string, date: string, token: string) =>
    api
      .get<Omit<RescheduleOptions, 'editable'> & { editable: string[] | null }>(
        `/api/admin/orders/${orderId}/reschedule-options?date=${encodeURIComponent(date)}`,
        token,
      )
      .then((raw): RescheduleOptions => ({ ...raw, editable: toRescheduleEditable(raw.editable) })),

  rescheduleOrder: (orderId: string, body: RescheduleRequest, token: string) =>
    api.post<AdminOrderDetailResponse>(`/api/admin/orders/${orderId}/reschedule`, body, token),

  markInProgress: (orderId: string, token: string) =>
    api.post<AdminOrderDetailResponse>(`/api/admin/orders/${orderId}/mark-in-progress`, {}, token),

  markComplete: (orderId: string, token: string) =>
    api.post<AdminOrderDetailResponse>(`/api/admin/orders/${orderId}/mark-complete`, {}, token),

  // PUT .../cancel — surfaced in the UI as "Send to disputes" (sets status DISPUTED).
  sendToDisputes: (orderId: string, reason: string, token: string) =>
    api.put<AdminOrderResponse>(`/api/admin/orders/${orderId}/cancel`, { reason }, token),

  resolveDispute: (orderId: string, request: ResolveDisputeRequest, token: string) =>
    api.post<AdminOrderDetailResponse>(`/api/admin/orders/${orderId}/resolve`, request, token),

  getCustomerVehicles: (customerId: string, token: string) =>
    api.get<RawVehicleResponse[]>(`/api/admin/users/${customerId}/vehicles`, token)
      .then((vehicles) =>
        (vehicles || []).map(({ default: isDefault, ...rest }): VehicleResponse => ({
          ...rest,
          isDefault,
        }))),
};
